import React, { useRef } from 'react';
import Box from '@mui/material/Box';
import Popper from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import moment from 'moment';
import { getAuthData } from '../../../../../../../services/storage/LocalStorage';


const DrawAttentionIcon = styled(Box)(({ theme }) => ({
  border: theme.spacing(1),
  borderRadius: theme.spacing(0.5),
  height: theme.spacing(2.25),
  width: theme.spacing(2.25),
  marginTop: '6px',
  marginRight: '18px',
  backgroundColor: '#147e40',
  color: 'white',
  fontSize: theme.spacing(24),
}));

const Caption = styled('div')(() => ({
  fontSize: '10px',
  marginBottom: '10px',
}));


const PopoverContainer = styled(Popper)(({ theme }) => ({
  pointerEvents: 'none',
  padding: theme.spacing(2),
}));

const MainContainer = styled('div')(() => ({ height: '100%' }));

const EventTitle = styled('div')(() => ({
  pointerEvents: 'none',
  '& span': { fontWeight: 'bold' },
}));


const EventCalendarComponent = ({ event, changeSelectedEventRef, view }) => {
  const { content } = event;
  const wrapperRef = useRef(null);
  const defaultUserTimezone = 'Etc/UTC';
  const { user: { userTimezone } } = getAuthData();
  const tz = userTimezone || defaultUserTimezone;

  const formattedStart = moment(event.start).tz(tz).format('LLLL');
  const formattedEnd = moment(event.end).tz(tz).format('LT');

  const [anchorEl, setAnchorEl] = React.useState(null);

  const handlePopoverOpen = (e) => {
    setAnchorEl(e.currentTarget);
    changeSelectedEventRef(event.id);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'mouse-over-popover' : undefined;

  const renderContentFiledValues = ({ type, value, fileName }) => {
    let tempValue = '';
    switch (type) {
      case 'text_field':
      case 'number':
      case 'text_area':
      case 'radiobutton':
        tempValue = value;
        break;
      case 'dropdown':
      case 'checkbox':
      case 'switch':
      case 'multi_selection':
        tempValue = value;
        break;
      case 'file_field':
        tempValue = fileName;
        break;
      case 'buttons_field':
        tempValue = value?.text || '';
        break;
      case 'date':
        tempValue = value ? moment(value).format('ll') : '';
        break;
      case 'time':
        tempValue = value ? moment(value).format('LT') : '';
        break;
      case 'datetime':
        tempValue = value ? moment(value).format('lll') : '';
        break;
      case 'password_field':
      case 'slider':
      case 'image_field':
      case 'lookup':
        tempValue = value || '';
        break;
      default:
        break;
    }
    return tempValue;
  };

  return (
    <MainContainer
      ref={wrapperRef}
      onMouseEnter={handlePopoverOpen}
      onMouseLeave={handlePopoverClose}>
      {view !== 'month' ? (
        <>
          <Typography variant='subtitle2'>{event.title}</Typography>
          <Caption>
            {moment(event.start).format('LT')}
            {' '}
            -
            {' '}
            {moment(event.end).format('LT')}
          </Caption>
          {content.map((item) => (
            <EventTitle
              key={item.id}
              aria-owns={open ? 'mouse-over-popover' : undefined}
              aria-haspopup='true'>
              <Typography variant='caption' gutterBottom>
                {renderContentFiledValues(item)}
              </Typography>
            </EventTitle>
          ))}
        </>
      ) : (
        <EventTitle
          aria-owns={open ? 'mouse-over-popover' : undefined}
          aria-haspopup='true'>
          <Typography variant='caption' display='block'>
            {event.title}
          </Typography>
        </EventTitle>
      )}

      <PopoverContainer
        id={id}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        onClose={handlePopoverClose}>
        <div className='p-3 rounded-7'>
          <DrawAttentionIcon />
          <div className=''>
            <div xs={12}>
              <h5 className='text-md'>{event.title}</h5>
              <p className="text-sm" gutterBottom>
                {formattedStart}
                {' '}
                -
                {' '}
                {formattedEnd}
              </p>
            </div>
            {content.map((elem) => (
              <div className='mb-1 flex justify-between bg-purple-100 p-2 rounded-7' xs={12} key={elem.id}>
                <h5 className='text-xs' display='block'>
                  {elem.title}
                </h5>
                <p className='font-semibold ms-3 text-xs' gutterBottom>
                  {renderContentFiledValues(elem)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </PopoverContainer>
    </MainContainer>
  );
};

export default EventCalendarComponent;
